import { type Database } from "@fitness-app/supabase";

import { type MobileUpdateType } from "./AppRelease";
import { type AppAccessType } from "./Automation";
import { type ChatType } from "./Chat";
import { type ClientAdvisorConfigData } from "./ClientAdvisorConfig";
import { type ClientRatingConfigData } from "./ClientRating";
import { type CurrentOfferConfigData } from "./CurrentOfferConfig";
import { type FreeMonthConfigData } from "./FreeMonthConfig";
import { type SalesCallConfigData } from "./SalesCallConfig";
import { type TraineeAiReportConfigData } from "./TraineeAiReport";

export type TrainerConfigEntity = Database["public"]["Tables"]["trainer_config"]["Row"];
export type AppConfigEntity = Database["public"]["Tables"]["app_config"]["Row"];

export type AnnouncementChannelType = ChatType.OneWayMarketing | ChatType.TwoWayMarketing;

/**
 * Tenant-level settings of the additional announcement chat channel (TRE-801).
 * The channel itself is created per client; this config only decides whether new
 * clients get one, what it is called and whether clients can reply.
 */
export interface AnnouncementChannelConfig {
  enabled: boolean;
  /** Shown to clients as the channel name; blank falls back to DEFAULT_ANNOUNCEMENT_CHANNEL_NAME. */
  channelName: string | null;
  channelType: AnnouncementChannelType;
}

export const DEFAULT_ANNOUNCEMENT_CHANNEL_NAME = "Ogłoszenia i wskazówki";

export interface TrainerConfig extends TrainerConfigEntity {
  config: {
    limit?: number;
    announcementChannel?: AnnouncementChannelConfig | null;
  };
  mealsPlanTags: string[];
  trainingProgramTags: string[];
  mealDaysTags: string[];
  traineeTags: string[];
  workoutTags: string[];
  specializationTags: string[];
  exercisesTags: string[];
  supportRequestTags: string[];
}

export enum AppConfigType {
  APP_VERSION = "app_version",
  CLIENT_RATING = "client_rating",
  CLIENT_ADVISOR = "client_advisor",
  TRAINEE_AI_REPORT = "trainee_ai_report",
  FREE_MONTH = "free_month",
  SALES_CALL = "sales_call",
  CURRENT_OFFER = "current_offer",
}

export interface AppVersionData {
  current: string;
  /** Delivery method of this release, not the update path for every installed binary. */
  updateType?: MobileUpdateType | null;
  minRequired: string;
  versionsWithIssues: string[];
}

export interface AppVersionByPlatform {
  ios: AppVersionData | null;
  android: AppVersionData | null;
  web: AppVersionData | null;
  admin: AppVersionData | null;
}

export enum FeatureStatus {
  Enabled = "enabled",
  Disabled = "disabled",
  Maintenance = "maintenance",
}

export type FeatureStatusPerAccessType = Record<AppAccessType, FeatureStatus>;

export interface ClientFeaturesFlags {
  languageSelection?: FeatureStatus | FeatureStatusPerAccessType;
  aiAnalyzer?: FeatureStatus | FeatureStatusPerAccessType;
  allowDishReplaceBetweenMeals?: FeatureStatus | FeatureStatusPerAccessType;
  disableRecording?: FeatureStatus | FeatureStatusPerAccessType;
  /** TRE-1603 — client-advisor (low-ticket 2.0) gate. Stamped per-trainee from the
   *  automation's `advisorEnabled` designation; absent = legacy client (no advisor). */
  clientAdvisor?: FeatureStatus | FeatureStatusPerAccessType;
  /** TRE-2184 — Disabled = advisor-only client, no trainer chat channel (see TraineeFeaturesFlags). */
  trainerChat?: FeatureStatus | FeatureStatusPerAccessType;
  /** TRE-2414 - "Edit product / add photo" on a product in the diet tracker ("Report an error" is for everyone). */
  productCorrections?: FeatureStatus | FeatureStatusPerAccessType;
}

export enum BackendFeaturesFlagNames {
  workoutActivitiesReplication = "replicate_workouts_activities",
}

export interface TraineeFeaturesFlags {
  languageSelection?: FeatureStatus;
  aiAnalyzer?: FeatureStatus;
  allowDishReplaceBetweenMeals?: FeatureStatus;
  disableRecording?: FeatureStatus;
  /** TRE-1603 — client-advisor (low-ticket 2.0) gate. Set to Enabled when the client is
   *  added to an automation whose `advisorEnabled` designation is on; unset for legacy. */
  clientAdvisor?: FeatureStatus;
  /** TRE-2184 — Disabled when the client's automation runs advisor-only (trainerChatEnabled=false):
   *  no trainer chat channel is created and scheduled messages open advisor conversations.
   *  Unset = a trainer channel as always. */
  trainerChat?: FeatureStatus;
  /** TRE-2414 - see ClientFeaturesFlags.productCorrections; set per trainee to switch it on early. */
  productCorrections?: FeatureStatus;
}

export interface AppVersionConfig extends Omit<AppConfigEntity, "data" | "clientFeatures"> {
  type: AppConfigType.APP_VERSION;
  data: AppVersionByPlatform;
  clientFeatures: null | ClientFeaturesFlags;
}

export interface ClientRatingAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.CLIENT_RATING;
  data: ClientRatingConfigData;
  clientFeatures: null;
}

/** TRE-1613 — client-advisor configuration (model choice) managed from the admin Advisor settings tab. */
export interface ClientAdvisorAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.CLIENT_ADVISOR;
  data: ClientAdvisorConfigData;
  clientFeatures: null;
}

/** TRE-2192 — free-month offers (which product each landing offer activates). */
export interface FreeMonthAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.FREE_MONTH;
  data: FreeMonthConfigData;
  clientFeatures: null;
}

/** TRE-2465 — the Calendly event booked on `/rozmowa`. */
export interface SalesCallAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.SALES_CALL;
  data: SalesCallConfigData;
  clientFeatures: null;
}

/** TRE-2388 — the promotion behind the stable link `/aktualna-oferta`. */
export interface CurrentOfferAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.CURRENT_OFFER;
  data: CurrentOfferConfigData;
  clientFeatures: null;
}

/** Trainee AI report (TraineeProfile) — model choice managed from the admin app-config page. */
export interface TraineeAiReportAppConfig extends Omit<AppConfigEntity, "data" | "clientFeatures" | "type"> {
  type: AppConfigType.TRAINEE_AI_REPORT;
  data: TraineeAiReportConfigData;
  clientFeatures: null;
}
